// import uuid from "uuid/v4";

import { request } from "graphql-request";
import { aktivoOnBoard } from "./auth";
import { scoreStats, sleepStats, stepsStats, allStats } from "./queries";

import {
  AllStatsResponse,
  ScoreStatsResponse,
  SleepStatsResponse,
  StepsStatsResponse,
} from "./types/pa_dataset";

import { EmmaFactory } from "@axa-asia/hk-emma-js";

import { MemeberDataResponse } from "./types/member_data";

import {
  EnrolledChallengesDataResponse,
  UnEnrolledChallengesDataResponse,
} from "./types/challenge_enrolled_list";

import { getClientConfig } from "./utils";

import { throttle } from "lodash";

const isEmmaApp = /EMMA( [A-Za-z]+)?\/([0-9]+\.){3}[0-9]{8,10}/g.test(
  navigator.userAgent
);

type aktivoIdTokenPromise = Promise<{ token: string; id: string }>;

type AktivoAPIClientCache = {
  aktivoId: string;
  aktivoAccessToken: string;
  initialization: Promise<void> | null;
  errorCode: string;
};

const cached: AktivoAPIClientCache = {
  aktivoAccessToken: "",
  aktivoId: "",
  initialization: null,
  errorCode: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type anyObject = { [key: string]: any };

type ReqCallback = () => Promise<anyObject>;
type ReqWithAuthCallback = (reqOpts: RequestInit) => Promise<anyObject>;

type gqlQuery =
  | typeof scoreStats
  | typeof sleepStats
  | typeof stepsStats
  | typeof allStats;

class AktivoAPIClient {
  emmaToken: string;
  aktivoId: string | null = null;
  aktivoAccessToken: string | null = null;
  onError: (n: string) => void;
  initFailed = false;
  mock = getClientConfig().MOCK_AKTIVO_API_CLIENT === "true";

  aktivoEndpoint =
    getClientConfig().AKTIVO_USE_PROXY === "true"
      ? window.origin + "/proxy/aktivo"
      : getClientConfig().AKTIVO_ENDPOINT ||
        "https://api.hkpreprod.aktivolabs.com";

  setToken = (token: string, id: string) => {
    cached.aktivoAccessToken = token;
    cached.aktivoId = id;
    this.aktivoAccessToken = cached.aktivoAccessToken;
    this.aktivoId = cached.aktivoId;
  };

  restApiHandler = (res: Response) => {
    if (res.ok) return res.json();
    this.onError("EXP-A01");
    throw new Error(`HTTP ${res.status}`);
  };

  aktivoApiErrorHandler = (e: Error) => {
    this.onError("EXP-A01");
    throw e;
  };

  init: (token: string, onError?: (n: string) => void) => Promise<void> =
    async (token, onError) => {
      cached.errorCode = null;
      this.emmaToken = token;

      if (typeof onError === "function") {
        this.onError = throttle((errorCode: string) => {
          onError(errorCode);
        }, 300);
      } else {
        if (typeof this.onError === "function") {
          // nothing
        } else {
          this.onError = () => null;
        }
      }

      if (cached.initialization) {
        return cached.initialization
          .then(() => {
            if (!cached.aktivoAccessToken || !cached.aktivoId) {
              console.error("Failed to get token from Cache");
              this.initFailed = true;
            } else {
              this.setToken(cached.aktivoAccessToken, cached.aktivoId);
              this.initFailed = false;
            }
          })
          .catch(() => {
            console.error("Failed to get token from Cache");
            this.initFailed = true;
          });
      } else {
        const p = isEmmaApp ? this.initMobile(token) : this.initWeb(token);

        cached.initialization = p
          .then(({ token, id }) => {
            this.setToken(token, id);
            this.initFailed = false;
          })
          .catch((error) => {
            console.error(error);
            this.initFailed = true;
            this.onError(cached.errorCode || "EXP-E01");
          });

        return cached.initialization;
      }
    };

  initWeb: (token: string) => aktivoIdTokenPromise = async (token) => {
    cached.errorCode = null;

    const res = await aktivoOnBoard(
      getClientConfig().ALAKE_API_ENDPOINT,
      {
        token,
      },
      (errorCode: string) => {
        cached.errorCode = errorCode;
        this.onError(errorCode);
      }
    );

    if ("retrieveAktivoToken" in res) {
      return {
        token: res.retrieveAktivoToken.access_token,
        id: res.retrieveAktivoToken.aktivo_id,
      };
    } else {
      let errorCode = "EXP-E01";

      if ("response" in res) {
        if ("reasonCode" in res.response) {
          errorCode = res.response.reasonCode;
        } else {
          errorCode = "EXP-E01";
        }
      }

      this.onError(errorCode);
      cached.errorCode = errorCode;
    }
  };

  initMobile: (token: string) => aktivoIdTokenPromise = async (token) => {
    try {
      const e = await EmmaFactory.createInstance();

      const aktivoConfig = e.aktivoConfig;

      if (aktivoConfig.aktivoId && aktivoConfig.auth.accessToken) {
        return {
          token: aktivoConfig.auth.accessToken,
          id: aktivoConfig.aktivoId,
        };
      } else {
        throw new Error("Emma JS Failed to return token");
      }
    } catch (error) {
      console.error(error);
      return this.initWeb(token);
    }
  };

  constructor(token: string, onError?: (n: string) => void) {
    this.init(token, onError);
  }

  request = (callback: ReqCallback) => {
    if (this.initFailed) {
      cached.initialization = this.init(this.emmaToken);
    }

    return cached.initialization
      .then(() => {
        if (this.initFailed) {
          throw new Error(
            "Unable to fetch without successful aktivoOnBoard API"
          );
        }
        return callback();
      })
      .catch((error) => {
        throw error;
      });
  };

  requestWithAuth = (callback: ReqWithAuthCallback, lang = "en") => {
    return this.request(() => {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", "Bearer " + this.aktivoAccessToken);
      myHeaders.append("Accept-Language", lang);
      // myHeaders.append("x-axahk-msgid", uuid());

      const requestOptions: RequestInit = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
        mode: "cors",
      };

      return callback(requestOptions);
    });
  };

  requestGraphql = (query: gqlQuery, variables: anyObject) => {
    return this.request(() => {
      return request({
        url: this.aktivoEndpoint + "/graphql/user/" + this.aktivoId,
        document: query,
        variables: variables,
        requestHeaders: {
          authorization: "Bearer " + this.aktivoAccessToken,
          // "x-axahk-msgid": uuid(),
        },
      })
        .then((data) => ({ data }))
        .catch(this.aktivoApiErrorHandler);
    });
  };

  getMemberData = () => {
    if (this.mock)
      return import("./mock/member_data.json").then((res) => res.default);

    return this.requestWithAuth((requestOptions) => {
      return fetch(
        this.aktivoEndpoint + "/api/members/" + this.aktivoId,
        requestOptions
      )
        .then(this.restApiHandler)
        .catch(this.aktivoApiErrorHandler);
    }) as Promise<MemeberDataResponse>;
  };

  /**
   * Challenges
   */
  challengeSoloEnrolledList = (lang: "en" | "zh") => {
    if (this.mock)
      return import("./mock/challenge_enrolled_list_data.json").then(
        (res) => res.default
      );

    return this.requestWithAuth((requestOptions) => {
      return fetch(
        this.aktivoEndpoint + "/api/challengesolo/enrolledList",
        requestOptions
      )
        .then(this.restApiHandler)
        .catch(this.aktivoApiErrorHandler);
    }, lang) as Promise<EnrolledChallengesDataResponse>;
  };

  challengeSoloUnEnrolledList = (lang: "en" | "zh") => {
    if (this.mock)
      return import("./mock/challenge_enrolled_list_data.json").then(
        (res) => res.default
      );

    return this.requestWithAuth((requestOptions) => {
      return fetch(
        this.aktivoEndpoint + "/api/challengesolo/unEnrolledList",
        requestOptions
      )
        .then(this.restApiHandler)
        .catch(this.aktivoApiErrorHandler);
    }, lang) as Promise<UnEnrolledChallengesDataResponse>;
  };

  /**
   * Aktivo pa stats (graphql)
   */

  scoreStats = (variables: anyObject) => {
    if (this.mock)
      return import("./mock/pa_dataset.json").then((res) => res.default);

    return this.requestGraphql(
      scoreStats,
      variables
    ) as Promise<ScoreStatsResponse>;
  };

  sleepStats = (variables: anyObject) => {
    if (this.mock)
      return import("./mock/pa_dataset.json").then((res) => res.default);

    return this.requestGraphql(
      sleepStats,
      variables
    ) as Promise<SleepStatsResponse>;
  };

  stepsStats = (variables: anyObject) => {
    if (this.mock)
      return import("./mock/pa_dataset.json").then((res) => res.default);

    return this.requestGraphql(
      stepsStats,
      variables
    ) as Promise<StepsStatsResponse>;
  };

  allStats = (variables: anyObject) => {
    if (this.mock)
      return import("./mock/pa_dataset.json").then((res) => res.default);

    return this.requestGraphql(
      allStats,
      variables
    ) as Promise<AllStatsResponse>;
  };

  getToken = () => {
    return cached.initialization.then(() => {
      if (this.initFailed) throw new Error("Unable to get token");

      return {
        aktivoId: this.aktivoId || cached.aktivoId,
        aktivoAccessToken: this.aktivoAccessToken || cached.aktivoAccessToken,
      };
    });
  };
}

export { AktivoAPIClient };
